import React from 'react';
import styled from 'styled-components';
import { ArrowRight16, ArrowLeft16 } from '@honeybook/icons';
import { vars } from '../../theme';
import HBIconButton from '../HBIconButton/HBIconButton';
import { TDatepickerViewTypes } from './HBDatepicker.types';
import HBClickable from '../HBClickable/HBClickable';

const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];

interface Props {
    date: Date;
    decrease: Function;
    onDecreaseClick?: (date: Date, type: 'decrease') => void;
    increase: Function;
    onIncreaseClick?: (date: Date, type: 'increase') => void;
    viewType?: TDatepickerViewTypes;
    prevMonthButtonDisabled?: boolean;
    nextMonthButtonDisabled?: boolean;
    prevYearButtonDisabled?: boolean;
    nextYearButtonDisabled?: boolean;
    setViewType?: (type: TDatepickerViewTypes) => void;
}

// copied these functions from react-datepicker repo
function getYear(date: Date) {
    return date.getFullYear();
}

export function getYearsPeriod(date: Date, yearItemNumber = 12) {
    const endPeriod =
        Math.ceil(getYear(date) / yearItemNumber) * yearItemNumber;
    const startPeriod = endPeriod - (yearItemNumber - 1);
    return { startPeriod, endPeriod };
}

function headerText(viewType: TDatepickerViewTypes, date: Date) {
    switch (viewType) {
        case 'day':
            return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
        case 'month':
            return `${date.getFullYear()}`;
        case 'year': {
            const { startPeriod, endPeriod } = getYearsPeriod(date);
            return `${startPeriod} - ${endPeriod}`;
        }
    }
}

const CustomHeader = ({
    date,
    decrease,
    onDecreaseClick,
    increase,
    onIncreaseClick,
    viewType = 'day',
    setViewType,
    ...rest
}: Props) => {
    const decreaseCallback = () => {
        decrease();
        if (onDecreaseClick) {
            onDecreaseClick(date, 'decrease');
        }
    };
    const increaseCallback = () => {
        increase();
        if (onIncreaseClick) {
            onIncreaseClick(date, 'increase');
        }
    };

    const prevDisabled =
        viewType === 'day'
            ? rest.prevMonthButtonDisabled
            : rest.prevYearButtonDisabled;
    const nextDisabled =
        viewType === 'day'
            ? rest.nextMonthButtonDisabled
            : rest.nextYearButtonDisabled;

    const changeDatePickerType = () => {
        if (viewType === 'day') {
            setViewType('month');
        }
        if (viewType === 'month') {
            setViewType('year');
        }
    };

    const isClickable = viewType !== 'year';

    return (
        <Header>
            <Prev
                icon={ArrowLeft16}
                onClick={decreaseCallback}
                label={viewType === 'day' ? 'Previous Month' : 'Previous Year'}
                disabled={prevDisabled}
                testId="datepicker-prev--button"
            />
            {isClickable ? (
                <HBClickable
                    onClick={changeDatePickerType}
                    testIdName="datepicker-header"
                >
                    <HeaderContent>
                        <HeaderTitle isClickable={true}>
                            {headerText(viewType, date)}
                        </HeaderTitle>
                    </HeaderContent>
                </HBClickable>
            ) : (
                <HeaderTitle>{headerText(viewType, date)}</HeaderTitle>
            )}
            <Next
                icon={ArrowRight16}
                onClick={increaseCallback}
                label={viewType === 'day' ? 'Next Month' : 'Next Year'}
                disabled={nextDisabled}
                testId="datepicker-next--button"
            />
        </Header>
    );
};

export default CustomHeader;

// -------------------------------- Styles -------------------------------- //
const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    position: relative;
`;

const HeaderTitle = styled.span<{ isClickable?: boolean }>`
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${vars.colors.text.secondary};
    cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};

    ${({ isClickable }) =>
        isClickable &&
        `
        &:hover {
            background-color: ${vars.colors.surface.itemHover};
            padding: 4px 8px;
            margin: -4px;
            border-radius: ${vars.radius.xs};
        }
    `}
`;

const NavButton = styled(HBIconButton)`
    position: absolute;
    top: -10px;
`;

const Prev = styled(NavButton)`
    left: 0px;
`;

const Next = styled(NavButton)`
    right: 0px;
`;

const HeaderContent = styled.div`
    display: flex;
    gap: 4px;
`;
